import { type FieldPath, useWatch } from 'react-hook-form';
import { ROW_UNIQUE_KEY_NAME } from '../../../common/utils';
import { useSystemClassifierItem } from '../../../configuration';
import { SystemClassifierItemDto } from '../../../systemClassifier';
import { DataObjectDimensionType } from '../../types';
import { AlgorithmSourceDimensionFormDataType } from '../sources/schema/types';
import { AlgorithmJoinItemSelectFieldOptionGroup } from './AlgorithmJoinItemSelectField';
import {
  AlgorithmJoinItemValidFormDataType,
  AlgorithmJoinsValidFormDataType,
} from './schema/types';

export const getSelectedDimension = (
  dimensionOptions: AlgorithmJoinItemSelectFieldOptionGroup[],
  algorithmSourceId?: number,
  dimensionCode?: string,
): AlgorithmSourceDimensionFormDataType | undefined => {
  if (!algorithmSourceId || !dimensionCode) {
    return undefined;
  }

  return dimensionOptions.reduce<
    AlgorithmSourceDimensionFormDataType | undefined
  >((dimension, group) => {
    if (dimension) {
      return dimension;
    }

    for (const option of group.options) {
      if (
        'dimension' in option &&
        option.dimension?.code === dimensionCode &&
        option.algorithmSource.id === algorithmSourceId
      ) {
        return option.dimension;
      }
    }
  }, undefined);
};

export const changeAlgorithmJoinItemFormDataPerConstantSwitch = (
  currentItem: AlgorithmJoinItemValidFormDataType,
  role: 'source' | 'target',
  isConstant: boolean,
  classifierDataTypesById: Map<number, SystemClassifierItemDto>,
  dimension?: AlgorithmSourceDimensionFormDataType,
): AlgorithmJoinItemValidFormDataType => {
  if (role === 'source') {
    if (isConstant) {
      if (
        currentItem._variant === 'DIMENSION_DIMENSION' ||
        currentItem._variant === 'MAPPING_DIMENSION'
      ) {
        return changeAlgorithmJoinItemFormDataPerDimensionClassifierCodeFormatChange(
          {
            [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
            _variant: 'VALUE_DIMENSION',
            sourceValue: '',
            targetDimension: currentItem.targetDimension,
          },
          classifierDataTypesById,
          dimension,
        );
      }
      if (
        currentItem._variant === 'DIMENSION_VALUE' ||
        currentItem._variant === 'MAPPING_VALUE'
      ) {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'VALUE_DIMENSION', // flip
          sourceValue: '',
          targetDimension: '',
        };
      }
      if (currentItem._variant === 'DIMENSION_MAPPING') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'VALUE_MAPPING',
          sourceValue: '',
          algorithmMappingAlias: currentItem.algorithmMappingAlias,
        };
      }
    } else {
      if (currentItem._variant === 'VALUE_DIMENSION') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_DIMENSION',
          algorithmSourceId: 0,
          sourceDimension: '',
          targetDimension: currentItem.targetDimension,
        };
      }
      if (currentItem._variant === 'VALUE_MAPPING') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_MAPPING',
          algorithmSourceId: 0,
          sourceDimension: '',
          algorithmMappingAlias: currentItem.algorithmMappingAlias,
        };
      }
    }
  }

  if (role === 'target') {
    if (isConstant) {
      if (currentItem._variant === 'DIMENSION_DIMENSION') {
        return changeAlgorithmJoinItemFormDataPerDimensionClassifierCodeFormatChange(
          {
            [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
            _variant: 'DIMENSION_VALUE',
            algorithmSourceId: currentItem.algorithmSourceId,
            sourceDimension: currentItem.sourceDimension,
            targetValue: '',
          },
          classifierDataTypesById,
          dimension,
        );
      }
      if (currentItem._variant === 'MAPPING_DIMENSION') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'MAPPING_VALUE',
          algorithmMappingAlias: currentItem.algorithmMappingAlias,
          targetValue: '',
        };
      }
      if (currentItem._variant === 'VALUE_DIMENSION') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_VALUE', // flip
          algorithmSourceId: 0,
          sourceDimension: '',
          targetValue: '',
        };
      }
      if (currentItem._variant === 'DIMENSION_MAPPING') {
        return changeAlgorithmJoinItemFormDataPerDimensionClassifierCodeFormatChange(
          {
            [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
            _variant: 'DIMENSION_VALUE',
            algorithmSourceId: currentItem.algorithmSourceId,
            sourceDimension: currentItem.sourceDimension,
            targetValue: '',
          },
          classifierDataTypesById,
          dimension,
        );
      }
      if (currentItem._variant === 'VALUE_MAPPING') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_VALUE', // source becomes dimension (UI forces setIsSourceConstant(false))
          algorithmSourceId: 0,
          sourceDimension: '',
          targetValue: '',
        };
      }
    } else {
      if (currentItem._variant === 'DIMENSION_VALUE') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_DIMENSION',
          algorithmSourceId: currentItem.algorithmSourceId,
          sourceDimension: currentItem.sourceDimension,
          targetDimension: '',
        };
      }
      if (currentItem._variant === 'MAPPING_VALUE') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'MAPPING_DIMENSION',
          algorithmMappingAlias: currentItem.algorithmMappingAlias,
          targetDimension: '',
        };
      }
    }
  }

  throw new Error(
    `${role} constant switch from ${
      isConstant ? 'FALSE to TRUE' : 'TRUE to FALSE'
    } should not be possible when algorithm join item variant is ${
      currentItem._variant
    }`,
  );
};

export const changeAlgorithmJoinItemFormDataPerDimensionClassifierCodeFormatChange =
  (
    currentItem: AlgorithmJoinItemValidFormDataType,
    classifierDataTypesById: Map<number, SystemClassifierItemDto>,
    dimension?: AlgorithmSourceDimensionFormDataType,
  ): AlgorithmJoinItemValidFormDataType => {
    switch (currentItem._variant) {
      case 'DIMENSION_VALUE':
        const {
          targetValueFormat,
          targetValueDataType,
          ...nextDimensionValueItem
        } = currentItem;
        return isDimensionWithCodeFormat(dimension)
          ? {
              ...nextDimensionValueItem,
              targetValueFormat: dimension.classifier.codeFormat,
              targetValueDataType: classifierDataTypesById.get(
                dimension.classifier.dataTypeId,
              )?.code,
            }
          : nextDimensionValueItem;

      case 'VALUE_DIMENSION':
        const {
          sourceValueFormat,
          sourceValueDataType,
          ...nextValueDimensionItem
        } = currentItem;
        return isDimensionWithCodeFormat(dimension)
          ? {
              ...nextValueDimensionItem,
              sourceValueFormat: dimension.classifier.codeFormat,
              sourceValueDataType: classifierDataTypesById.get(
                dimension.classifier.dataTypeId,
              )?.code,
            }
          : nextValueDimensionItem;

      default:
        return currentItem;
    }
  };

export const changeAlgorithmJoinItemFormDataForDb = (
  options: {
    currentItem: AlgorithmJoinItemValidFormDataType;
    side: 'source' | 'target';
  } & ({ algorithmMappingAlias: string } | { algorithmSourceId: number }),
): AlgorithmJoinItemValidFormDataType => {
  const { currentItem, side } = options;

  if ('algorithmMappingAlias' in options) {
    const { algorithmMappingAlias } = options;

    if (side === 'source') {
      if (currentItem._variant === 'DIMENSION_DIMENSION') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'MAPPING_DIMENSION',
          algorithmMappingAlias,
          targetDimension: currentItem.targetDimension,
        };
      }
      if (currentItem._variant === 'DIMENSION_VALUE') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'MAPPING_VALUE',
          algorithmMappingAlias,
          targetValue:
            typeof currentItem.targetValue === 'string'
              ? currentItem.targetValue
              : '',
        };
      }
      if (currentItem._variant === 'DIMENSION_MAPPING') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'MAPPING_DIMENSION',
          algorithmMappingAlias,
          targetDimension: '',
        };
      }
    }

    if (side === 'target') {
      if (currentItem._variant === 'DIMENSION_DIMENSION') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_MAPPING',
          algorithmSourceId: currentItem.algorithmSourceId,
          sourceDimension: currentItem.sourceDimension,
          algorithmMappingAlias,
        };
      }
      if (currentItem._variant === 'VALUE_DIMENSION') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'VALUE_MAPPING',
          sourceValue:
            typeof currentItem.sourceValue === 'string'
              ? currentItem.sourceValue
              : '',
          algorithmMappingAlias,
        };
      }
      if (currentItem._variant === 'MAPPING_DIMENSION') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_MAPPING',
          algorithmSourceId: null!,
          sourceDimension: '',
          algorithmMappingAlias,
        };
      }
    }
  } else {
    const { algorithmSourceId } = options;

    if (side === 'source') {
      if (currentItem._variant === 'MAPPING_DIMENSION') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_DIMENSION',
          algorithmSourceId,
          sourceDimension: '',
          targetDimension: currentItem.targetDimension,
        };
      }
      if (currentItem._variant === 'MAPPING_VALUE') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_VALUE',
          algorithmSourceId,
          sourceDimension: '',
          targetValue: currentItem.targetValue,
        };
      }
      if (currentItem._variant === 'DIMENSION_DIMENSION') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_DIMENSION',
          algorithmSourceId,
          sourceDimension: currentItem.sourceDimension,
          targetDimension: currentItem.targetDimension,
        };
      }
      if (currentItem._variant === 'DIMENSION_VALUE') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_VALUE',
          algorithmSourceId,
          sourceDimension: currentItem.sourceDimension,
          targetValue: currentItem.targetValue,
        };
      }
      if (currentItem._variant === 'DIMENSION_MAPPING') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_MAPPING',
          algorithmSourceId,
          sourceDimension: currentItem.sourceDimension,
          algorithmMappingAlias: currentItem.algorithmMappingAlias,
        };
      }
    }

    if (side === 'target') {
      if (currentItem._variant === 'DIMENSION_MAPPING') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'DIMENSION_DIMENSION',
          algorithmSourceId: currentItem.algorithmSourceId,
          sourceDimension: currentItem.sourceDimension,
          targetDimension: '',
        };
      }
      if (currentItem._variant === 'VALUE_MAPPING') {
        return {
          [ROW_UNIQUE_KEY_NAME]: currentItem[ROW_UNIQUE_KEY_NAME],
          _variant: 'VALUE_DIMENSION',
          sourceValue: currentItem.sourceValue,
          targetDimension: '',
        };
      }
    }
  }

  throw new Error(
    `DB column/mapping switch for ${side} side ${'algorithmMappingAlias' in options ? `to mapping "${options.algorithmMappingAlias}"` : 'to DB column'} is not supported when variant is ${currentItem._variant}`,
  );
};

export const isDimensionWithCategories = (
  dimension?: AlgorithmSourceDimensionFormDataType,
): dimension is AlgorithmSourceDimensionFormDataType =>
  Boolean(
    dimension?.classifier?.hasCategories ||
      dimension?.dimensionTypeCode ===
        DataObjectDimensionType.SUBJECT_ATTRIBUTE,
  );

export const isDimensionWithCodeFormat = (
  dimension?: AlgorithmSourceDimensionFormDataType,
): dimension is AlgorithmSourceDimensionWithCodeFormatFormDataType =>
  Boolean(
    dimension?.classifier?.codeFormat || dimension?.classifier?.dataTypeId,
  );

export const useIsDimensionWithDataType = (
  dataTypeCode: 'TEXT' | 'DATE' | 'NUMBER',
) => {
  const dateDataType = useSystemClassifierItem(
    'CLASSIFIER_DATA_TYPE',
    dataTypeCode,
  );

  return (
    dimension?: AlgorithmSourceDimensionFormDataType,
  ): dimension is AlgorithmSourceDimensionWithCodeFormatFormDataType =>
    Boolean(
      dimension?.classifier?.dataTypeId &&
        dateDataType &&
        dimension.classifier.dataTypeId === dateDataType.id,
    );
};

type AlgorithmSourceDimensionWithCodeFormatFormDataType = Omit<
  AlgorithmSourceDimensionFormDataType,
  'classifier'
> & {
  classifier: Omit<
    Required<AlgorithmSourceDimensionFormDataType>['classifier'],
    'codeFormat' | 'dataTypeId'
  > & {
    codeFormat: string;
    dataTypeId: number;
  };
};

export const useWatchAlgorithmJoinValue = <
  N extends FieldPath<AlgorithmJoinsValidFormDataType>,
>(
  name: N,
) => useWatch<AlgorithmJoinsValidFormDataType, typeof name>({ name });
